<template>
  <v-container>
    <v-row>
      <v-col>
        <v-container class="price-table">
          <v-row justify="start" align="start" align-content="start">
            <v-col>{{$t('common:facility')}}</v-col>
            <v-col>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <span
                        v-bind="attrs"
                        v-on="on"
                    >
                      {{$t('common:cost')}}*
                    </span>
                </template>
                <span>{{$t('book/facility:cost-analysis.cost-without-vat')}}</span>
              </v-tooltip>
            </v-col>
            <v-col>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <span
                        v-bind="attrs"
                        v-on="on"
                    >
                      {{$t('book/facility:cost-analysis.actual-cost')}}*
                    </span>
                </template>
                <span>{{$t('book/facility:cost-analysis.cost-without-vat')}}</span>
              </v-tooltip>
            </v-col>
            <v-col>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <span
                        v-bind="attrs"
                        v-on="on"
                    >
                      {{$t('common:difference')}}*
                    </span>
                </template>
                <span>{{$t('book/facility:cost-analysis.cost-without-vat')}}</span>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row justify="start" align="start" align-content="start" v-for="(facility, index) in facilities" :key="facility.name + index">
            <v-col><a :href="getFacilityUrl(facility.objId)" target="_blank">{{ facility.name }}</a></v-col>
            <v-col>{{ facility.cost }}</v-col>
            <v-col>{{ facility.actualCost }}</v-col>
            <v-col><div :class="getClassColor(facility)">{{(facility.actualCost - facility.cost).toFixed(2)}}</div></v-col>
          </v-row>
        </v-container>
      </v-col>
      <v-col>
        <v-container>
          <v-row>
            <v-col>
              {{$t('book/facility:cost-analysis.total-cost-without-vat')}}
            </v-col>
            <v-col class="primary--text">
              {{totalCost.toFixed(2)}}
            </v-col>
          </v-row>
          <v-row>
            <v-col >
              {{$t('book/facility:cost-analysis.total-cost-with-vat')}}
            </v-col>
            <v-col class="primary--text">
              {{(totalCost + totalVat).toFixed(2)}}
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              {{$t('book/facility:cost-analysis.vat')}}
            </v-col>
            <v-col class="primary--text">
              {{totalVat.toFixed(2)}}
            </v-col>
          </v-row>
          <v-row justify="end">
            <v-col md="1">
              <v-btn
                  elevation="2"
                  icon
                  outlined
                  color="primary"
                  @click="isPieChart = !isPieChart"
              >
                <v-icon> {{isPieChart ? 'bar_chart' : 'pie_chart'}} </v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-row v-if="isPieChart && showChart">
            <v-col>
              <h3>{{$t('common:cost')}}</h3>
              <VueApexCharts width="500" type="pie" :options="pieChartOptions" :series="pieSeries"></VueApexCharts>
            </v-col>
          </v-row>
          <v-row v-if="isPieChart && showChart">
            <v-col>
              <h3>{{$t('book/facility:cost-analysis.actual-cost')}}</h3>
              <VueApexCharts width="500" type="pie" :options="pieChartOptions" :series="pieManualSeries"></VueApexCharts>
            </v-col>
          </v-row>
          <v-row v-if="!isPieChart && showChart">
            <v-col>
              <VueApexCharts height="350" type="bar" :options="barChartOptions" :series="barSeries"></VueApexCharts>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>

import VueApexCharts from 'vue-apexcharts';
import CostAnalysisService from '@/modules/book/services/cost-analysis-service';
import Utils from "@/modules/core/services/utils";

export default {
  name: 'BuildingCostAnalysisTab',
  components: {VueApexCharts},
  data() {
    return {
      isPieChart: true,
      pieSeries: [],
      pieManualSeries: [],
      barSeries: [],
      pieChartOptions: {
        chart: {
          width: 350,
        },
        labels: [],
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      },
      barChartOptions: {
        chart: {
          height: 450
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          },
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: [],
        },
        yaxis: {
          title: {
            text: '€'
          }
        },
        fill: {
          opacity: 1
        }
      },
      facilities: [],
      showChart: false,
      totalCost: 0,
      totalVat: 0
    }
  },
  methods: {
    loadFacilities() {
      this.showChart = false;
      this.clearCharts();

      let buildingId = this.$route.params.id;
      CostAnalysisService.readBuildAnalysis(buildingId,
          (response) => {
            this.fillAnalysis(response.data);
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure',
                {name: this.$t('book/facility:cost-analysis.cost-analysis')}));
          }
      );


    },
    clearCharts() {
      this.pieSeries = [];
      this.pieManualSeries = [];
      this.pieChartOptions.labels = [];
      this.barChartOptions.xaxis.categories = [];
      this.barSeries = [];
      this.totalCost = 0;
      this.totalVat = 0;
    },
    fillAnalysis(facilities) {
      this.facilities = [];
      let cost = [];
      let manual = [];

      for(let i = 0; i < facilities.length; i++) {
        this.pieChartOptions.labels.push(facilities[i].name);
        this.pieSeries.push(parseFloat(facilities[i].cost));
        this.pieManualSeries.push(parseFloat(facilities[i].actualCost));
        cost.push(parseFloat(facilities[i].cost));
        manual.push(parseFloat(facilities[i].actualCost));
        this.barChartOptions.xaxis.categories.push(facilities[i].name)
        this.setFacility(facilities[i]);
        this.totalCost += parseFloat(facilities[i].cost);
        this.totalVat += parseFloat(facilities[i].totalVat);
      }

      this.barSeries.push({name: this.$t('common:cost'), data: cost});
      this.barSeries.push({name: this.$t('book/facility:cost-analysis.actual-cost'), data: manual});
      this.showChart = true;
    },
    getClassColor(item) {
      let costDifferenceClass = 'cost-difference '
      costDifferenceClass += item.actualCost - item.cost > 0 ? 'red' : 'green';
      return costDifferenceClass;
    },
    setFacility(facility) {
      this.facilities.push({
        name: facility.name,
        cost: parseFloat(facility.cost).toFixed(2),
        actualCost: parseFloat(facility.actualCost).toFixed(2),
        objId: facility.objId
      })
    },
    getFacilityUrl(facilityId) {
      return this.$router.resolve({name: 'FacilityCostAnalysisTab', params:{id: facilityId}}).href;
    }
  },
  mounted() {
    this.loadFacilities();
  }
}
</script>

<style lang="scss">
.price-table {

  .cost-difference{
    height: 100%;
    width: 50%;
  }

  .actual-cost {
    padding: 0;

    .v-input {
      padding: 0;
      margin: 0;
    }
  }
}
</style>